import React, { useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext.jsx'
import { consumeAuthEntrypoint, trackUiEvent } from '../utils/uiAnalytics.js'
import { Button, Card, CardTitle, Input } from '../design-system/primitives/index.js'
import StateBanner from './StateBanner/StateBanner.jsx'

const passwordToggleStyle = {
  position: 'absolute',
  right: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '4px 8px',
  color: 'var(--text-secondary)',
  fontSize: 'var(--text-base)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 'auto',
  minWidth: 'auto',
}

function normalize(s) {
  return (s ?? '').trim()
}

function Register({
  onRegisterSuccess,
  onSwitchToLogin,
  embedded = false,
  showTitle = true,
  submitLabel = 'Create account',
  routeToPantry = true,
  kitchenFraming = false,
}) {
  const { login } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const trimmedEmail = useMemo(() => normalize(email).toLowerCase(), [email])
  const canSubmit = trimmedEmail && password && !loading

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    setLoading(true)
    try {
      const res = await fetch('/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          password,
        }),
      })

      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const raw = data.detail
        const detail =
          typeof raw === 'object' && raw?.message
            ? raw.message
            : Array.isArray(raw)
            ? raw.map((d) => d.msg || d).join(' ')
            : raw || `Request failed (${res.status})`
        throw new Error(detail)
      }

      await login(trimmedEmail, password, true)
      if (routeToPantry) {
        try {
          sessionStorage.setItem('agent_post_auth_route_pantry', '1')
        } catch {
          // ignore
        }
      }
      trackUiEvent('auth_register_completed', { auth_entrypoint: consumeAuthEntrypoint() })
      onRegisterSuccess?.(data)
    } catch (err) {
      setError(err?.message || 'Failed to create account.')
    } finally {
      setLoading(false)
    }
  }

  const content = (
    <>
      {showTitle ? (
        <CardTitle style={{ marginBottom: 'var(--space-6)' }}>
          <i className="fas fa-user-plus icon-sm icon-primary icon-spacing-sm" aria-hidden="true"></i>{' '}
          {kitchenFraming ? 'Save your kitchen' : 'Start your kitchen'}
        </CardTitle>
      ) : null}

      {!embedded && showTitle ? (
        <p className="page-subtitle" style={{ marginBottom: 'var(--space-6)', marginTop: 0 }}>
          {kitchenFraming
            ? 'Create an account to keep this pantry, your recipes, and your preferences.'
            : 'Free account, email and password.'}
        </p>
      ) : null}

      <form id="register-form" onSubmit={handleSubmit} aria-label="Create account form">
        <div className="login-form-block">
          <label htmlFor="register-email" className="login-form-label">
            Email
          </label>
          <Input
            id="register-email"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            style={{ width: '100%', fontSize: 'var(--text-sm)' }}
            placeholder="name@domain.com"
            autoComplete="email"
          />
        </div>

        <div className="login-form-block">
          <label htmlFor="register-password" className="login-form-label">
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <Input
              id="register-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              style={{ width: '100%', paddingRight: '40px' }}
              placeholder="At least 8 characters"
              autoComplete="new-password"
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowPassword(!showPassword)}
              disabled={loading}
              style={{
                ...passwordToggleStyle,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.5 : 1,
              }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <i className={showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
            </Button>
          </div>
        </div>

        {error && (
          <StateBanner
            variant="urgent"
            headline="Couldn’t create your account"
            body={error}
            dismissible
            onDismiss={() => setError('')}
            role="alert"
          />
        )}

        <Button
          id="register-submit"
          type="submit"
          variant="primary"
          disabled={!canSubmit}
          className={loading ? 'loading' : ''}
          aria-label={loading ? 'Creating account' : submitLabel}
          style={{ width: '100%', fontSize: 'var(--text-base)', fontWeight: '500' }}
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i> Creating account...
            </>
          ) : (
            <>
              <i className="fas fa-user-plus"></i> {submitLabel}
            </>
          )}
        </Button>

        <div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
          <Button
            type="button"
            variant="link"
            className="link-cta-standalone"
            onClick={() => onSwitchToLogin?.()}
            disabled={loading}
            style={{ fontSize: 'var(--text-sm)' }}
            aria-label="Go to login"
          >
            Already have an account? Log in
          </Button>
        </div>
      </form>
    </>
  )

  if (embedded) {
    return content
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-6)',
        minHeight: '400px',
      }}
    >
      <Card style={{ maxWidth: '400px', width: '100%', position: 'relative' }}>
        {content}
      </Card>
    </div>
  )
}

export default Register
